import { Injectable } from '@angular/core';
import { CanActivate, CanActivateChild, CanLoad, Route, UrlSegment, ActivatedRouteSnapshot, RouterStateSnapshot, UrlTree, Router } from '@angular/router';
import { NGXLogger } from 'ngx-logger';
import { Observable } from 'rxjs';
import { AuthService, UserRole } from '../auth/auth.service';

/**
 * Checks whether the user is allowed to access the router based on the user's role.
 *
 * If used, the route or one of its ancestors must define some roles in its data which are allowed to access the route:
 *
 * ```typescript
 * data: {
 *   // Only users with the report role are allowed to access the route
 *   roles: [UserRole.Report]
 * },
 * ```
 */
@Injectable({
  providedIn: 'root'
})
export class RoleAccessGuard implements CanActivate, CanActivateChild, CanLoad {
  constructor(private auth: AuthService, private router: Router, private log: NGXLogger) {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    // Only perform checks if user is logged in, otherwise the redirection interferes with AuthGuard (/login vs. /logout)
    if (!this.auth.isLoggedIn) {
      return true;
    }

    const roles = this.getNearestRoleDefinition(route);

    if (roles == null) {
      this.log.error('RoleAccessGuard is used but no allowed roles were defined. Blocking route access.');
      this.auth.logoutObserved().subscribe();
      return this.router.parseUrl('/logout');
    }

    if (!roles.includes(this.auth.role)) {
      this.log.warn(
`Accessing the route ${state.url} is not allowed with the current user role.
Current role: ${this.auth.role}
Required role (one of): ${roles}`
      );

      this.auth.logoutObserved().subscribe();
      return this.router.parseUrl('/logout');
    }

    return true;
  }

  canActivateChild(childRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    return this.canActivate(childRoute, state);
  }

  canLoad(route: Route, segments: UrlSegment[]): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    // Only perform checks if user is logged in, otherwise the redirection interferes with AuthGuard (/login vs. /logout)
    if (!this.auth.isLoggedIn) {
      return true;
    }
    
    const roles = this.getNearestRoleDefinition(route);

    if (roles == null) {
      this.log.error('RoleAccessGuard is used but no allowed roles were defined. Blocking route access.');
      this.auth.logoutObserved().subscribe();
      return this.router.parseUrl('/logout');
    }

    if (!roles.includes(this.auth.role)) {
      this.log.warn(
`Accessing the route ${segments} is not allowed with the current user role.
Current role: ${this.auth.role}
Required role (one of): ${roles}`
      );
      this.auth.logoutObserved().subscribe();
      return this.router.parseUrl('/logout');
    }

    return true;
  }

  private getNearestRoleDefinition(route: Route|ActivatedRouteSnapshot): UserRole[]|null {
    let roles: UserRole[] = route.data.roles;

    if (!(route instanceof ActivatedRouteSnapshot)) {
      return roles;
    }

    // Try to find the next role definition in a parent (only the nearest ancestor definition is valid, others are ignored)
    if (roles == null) {
      let parent = route.parent;

      while (parent != null) {
        if (parent.data.roles != null) {
          roles = parent.data.roles;
          break;
        } else {
          parent = parent.parent;
        }
      }
    }

    return roles;
  }
}
